import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { requestNotificationPermission } from "./alerts";
import FibVwap from "./FibVwap";
import FibVwapGuarded from "./FibVwapGuarded";
import FibVwapSmart2 from "./FibVwapSmart2";
import GoldBacktest from "./GoldBacktest";
import ExpiryDay from "./ExpiryDay";
import SRLevels from "./SRLevels";
import PremiumAnalyzer from "./PremiumAnalyzer";
import OIMomentum from "./OIMomentum";
import StrategyBacktest from "./StrategyBacktest";
import SMCStrategy from "./SMCStrategy";
import StrategyScanner from "./StrategyScanner";
import StrategyOptions from "./StrategyOptions";
import MROptions from "./MROptions";
import Breakout915 from "./Breakout915";
import Reversal from "./Reversal";
import CryptoTab from "./CryptoTab";
import CryptoAnalytics from "./CryptoAnalytics";
import OBTracker from "./OBTracker";
import EventsTab from "./EventsTab";
import FvgScanner from "./FvgScanner";
import TradeAlerts from "./TradeAlerts";
import TradeMode from "./TradeMode";
import AiAnalyzer from "./AiAnalyzer";
import Logs from "./Logs";
import Analytics from "./Analytics";
import PnlCalendar from "./PnlCalendar";
import NeverRed from "./NeverRed";
import PriceAlerts from "./PriceAlerts";
import FinancialTargets from "./FinancialTargets";
import PnlCalc from "./PnlCalc";
import Dashboard, { lastActivity, DASH_SEEN_KEY } from "./Dashboard";
import ShadowPrem from "./ShadowPrem";

/* short two-tone beep on a new trade — WebAudio, no asset; browsers allow it
   after the first user interaction with the page */
let _audioCtx = null;
function alertSiren() {
  try {
    if (localStorage.getItem("dashSound") === "0") return;
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    [0, 0.25, 0.5, 0.75, 1.0, 1.25].forEach((t0, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "square"; o.frequency.value = i % 2 ? 660 : 990;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + 0.22);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t0); o.stop(ctx.currentTime + t0 + 0.24);
    });
  } catch {}
}
function tradeBeep() {
  try {
    if (localStorage.getItem("dashSound") === "0") return;
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const play = (freq, t0, dur) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t0); o.stop(ctx.currentTime + t0 + dur + 0.05);
    };
    play(880, 0, 0.18); play(1320, 0.2, 0.25);
  } catch { /* audio blocked until first interaction — fine */ }
}
import StrategyLog from "./StrategyLog";
import Positions from "./Positions";
import UnitTest from "./UnitTest";
import FiveEma from "./FiveEma";

const STRATEGY_TABS = [
  { id: "fib-nifty", label: "NIFTY", strategy: "fib-vwap", index: "NIFTY", desc: "Fib+VWAP on Nifty 50" },
  { id: "fib-banknifty", label: "BANK NIFTY", strategy: "fib-vwap", index: "BANKNIFTY", desc: "Fib+VWAP on Bank Nifty" },
  { id: "fib-sensex", label: "SENSEX", strategy: "fib-vwap", index: "SENSEX", desc: "Fib+VWAP on Sensex" },
];
// Tabs visible by default — everything else stays hidden behind the "More" toggle
// (hidden tabs' scanners keep running server-side regardless of UI visibility)
const CORE_TAB_IDS = new Set(["dashboard", "fib-nifty", "fib-banknifty", "fib-sensex", "guarded-nifty", "events", "analytics", "pnl-calendar", "never-red", "price-alerts", "crypto-analytics", "ob-tracker", "ai-analyze", "fin-targets", "pnl-calc", "prem-ab", "strategy-log", "logs", "telegram"]);

const ANALYTICS_TABS = [
  { id: "events", label: "Events" },
  { id: "analytics", label: "Indian Analytics" },
  { id: "crypto-analytics", label: "Crypto Analytics" },
  { id: "pnl-calendar", label: "P&L Calendar" },
  { id: "fin-targets", label: "Targets" },
  { id: "pnl-calc", label: "P&L Calc" },
  { id: "prem-ab", label: "Prem A/B" },
  { id: "strategy-log", label: "Strategy Log" },
];

const UTIL_TABS = [
  { id: "never-red", label: "Never-Red Lab" },
  { id: "ob-tracker", label: "OB Tracker" },
  { id: "price-alerts", label: "Price Alerts" },
  { id: "ai-analyze", label: "AI Analyzer" },
  { id: "strategy-scanner", label: "Strategy Scanner" },
  { id: "strategy-options", label: "Strategy Options" },
  { id: "mr-options", label: "MR-Options" },
  { id: "breakout915", label: "915 Breakout" },
  { id: "reversal", label: "Reversal" },
  { id: "five-ema", label: "5-EMA" },
  { id: "fvg-scanner", label: "FVG Scanner" },
  { id: "strategy-backtest", label: "Strategy Backtest" },
  { id: "smc-strategy", label: "SMC Strategy" },
  { id: "oi-momentum", label: "OI Momentum" },
  { id: "premium-analyzer", label: "Premium Analyzer" },
  { id: "sr-levels", label: "S&R Levels" },
  { id: "alerts", label: "Alerts" },
  { id: "unit-test", label: "Unit Tests" },
  { id: "logs", label: "Logs" },
  { id: "positions", label: "Positions" },
];

function App() {
  const [broker] = useState("zerodha");
  const API = "/api/zerodha";

  const [authenticated, setAuthenticated] = useState(false);
  const [step, setStep] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showAllTabs, setShowAllTabs] = useState(() => localStorage.getItem("showAllTabs") === "1");
  const toggleAllTabs = () => setShowAllTabs(prev => {
    const next = !prev;
    localStorage.setItem("showAllTabs", next ? "1" : "0");
    // If we're hiding tabs while standing on one of them, jump back to NIFTY
    if (!next && !CORE_TAB_IDS.has(activeTab)) setActiveTab("dashboard");
    return next;
  });
  const [killSwitch, setKillSwitch] = useState(false);
  const [dashUnseen, setDashUnseen] = useState(0);
  const knownTradeIds = useRef(null); // null until first poll (no beep on initial load)
  const [alertBanner, setAlertBanner] = useState(null); // latest unseen price-alert fire
  const seenFires = useRef(null);
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await axios.get("/api/price-alerts", { timeout: 10000 });
        const fires = r.data?.fires || [];
        if (seenFires.current === null) {
          seenFires.current = new Set(fires.map(f => f.id + f.time));
          return;
        }
        for (const f of fires) {
          const k = f.id + f.time;
          if (!seenFires.current.has(k)) {
            seenFires.current.add(k);
            setAlertBanner(f);
            alertSiren();
          }
        }
      } catch {}
    };
    poll();
    const h = setInterval(poll, 15000);
    return () => clearInterval(h);
  }, []);
  const knownCryptoIds = useRef(null);
  const [strategyStatus, setStrategyStatus] = useState({});
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);

  // Light/dark theme — dark is the default; choice persists across reloads
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  useEffect(() => {
    document.body.classList.toggle("light", theme === "light");
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Google auth gate — active only when the backend has GOOGLE_CLIENT_ID + ALLOWED_EMAILS
  const [authGate, setAuthGate] = useState("loading"); // loading | gate | ok
  const [googleClientId, setGoogleClientId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const cfg = await axios.get("/api/auth/config", { timeout: 8000 });
        if (!cfg.data.enabled) { setAuthGate("ok"); return; }
        setGoogleClientId(cfg.data.clientId);
        try {
          const me = await axios.get("/api/auth/me");
          setUserEmail(me.data.email);
          setAuthGate("ok");
        } catch { setAuthGate("gate"); }
      } catch { setAuthGate("ok"); } // backend down — fall through to normal error handling
    })();
  }, []);
  // if the 7-day session expires mid-use, any API 401 sends us back to the gate
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      if (error?.response?.status === 401 && googleClientId && !String(error.config?.url || "").includes("/api/auth/")) {
        setAuthGate("gate");
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, [googleClientId]);

  // Live status: auth heartbeat + strategy monitor
  const [authLive, setAuthLive] = useState(false); // true = Kite session valid
  const [monitorInfo, setMonitorInfo] = useState(null); // { monitoring, openCount, closedCount, marketOpen }
  const [serverTime, setServerTime] = useState("");

  useEffect(() => {
    if (!authenticated) return;
    const poll = async () => {
      // Auth heartbeat — try nifty-ltp, if it works Kite session is alive
      try {
        const ltp = await axios.get(`${API}/nifty-ltp`, { timeout: 5000 });
        setAuthLive(ltp.data.success === true);
      } catch {
        setAuthLive(false);
      }
      // Strategy monitor status
      try {
        // no strategy filter — the Dashboard badge needs guarded trades too;
        // the fib-vwap status pill counts are recomputed client-side below
        const res = await axios.get("/api/monitor/status", { timeout: 5000 });
        if (res.data.success) {
          setMonitorInfo(res.data);
          if (res.data.serverTime) setServerTime(res.data.serverTime);
          const fv = (res.data.trades || []).filter(t => t.strategy === "fib-vwap");
          setStrategyStatus({ "fib-vwap": {
            openCount: fv.filter(t => t.status === "OPEN").length,
            closedCount: fv.filter(t => t.status === "CLOSED").length,
            monitoring: res.data.monitoring,
          }});
          setKillSwitch(!!res.data.killSwitch);
          // New-trade sound: beep when a fib-vwap trade id appears that we haven't seen
          try {
            const fvAll = (res.data.trades || []).filter(t => String(t.strategy || "").startsWith("fib-vwap"));
            const ids = new Set(fvAll.map(t => String(t.id)));
            if (knownTradeIds.current !== null) {
              const fresh = fvAll.some(t => !knownTradeIds.current.has(String(t.id)));
              if (fresh) tradeBeep();
            }
            knownTradeIds.current = ids;
          } catch {}
          // Crypto paper trader: same beep on a new crypto trade (any tab)
          try {
            const cr = await axios.get("/api/crypto/trader/status", { timeout: 5000 });
            const cIds = new Set((cr.data?.openTrades || []).concat(cr.data?.closedTrades || []).map(t => `c${t.id}`));
            if (knownCryptoIds.current !== null) {
              if ([...cIds].some(id => !knownCryptoIds.current.has(id))) tradeBeep();
            }
            knownCryptoIds.current = cIds;
          } catch {}
          // Dashboard unseen-activity badge: fib-vwap activities newer than last seen
          try {
            const seen = Number(localStorage.getItem(DASH_SEEN_KEY) || 0);
            const n = (res.data.trades || []).filter(t => String(t.strategy || "").startsWith("fib-vwap") && lastActivity(t).ts > seen).length;
            setDashUnseen(n);
          } catch {}
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => clearInterval(id);
  }, [authenticated, API]);

  useEffect(() => {
    checkAuth();
    requestNotificationPermission();
  }, []);

  async function checkAuth() {
    try {
      const res = await axios.get(`${API}/auth-status`);
      if (res.data.authenticated) {
        setAuthenticated(true);
        setStep("dashboard");
      }
    } catch {}
  }

  async function handleLogin() {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/zerodha/login-url");
      if (res.data.success) {
        window.location.href = res.data.loginUrl;
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    }
    setLoading(false);
  }

  // Handle Zerodha redirect callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("zerodha_auth") === "success") {
      setAuthenticated(true);
      setStep("dashboard");
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const requestToken = params.get("request_token");
    if (requestToken) {
      axios.post("/api/zerodha/session", { request_token: requestToken }).then(res => {
        if (res.data.success) {
          setAuthenticated(true);
          setStep("dashboard");
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }).catch(err => {
        setError(err.response?.data?.message || "Zerodha session failed");
      });
      return;
    }

    const zerodhaError = params.get("zerodha_error");
    if (zerodhaError) {
      setError(`Zerodha: ${zerodhaError}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  async function handleLogout() {
    await axios.post(`${API}/logout`);
    setAuthenticated(false);
    setStep("login");
  }

  // Market ticker
  const [marketData, setMarketData] = useState(null);
  const [tickerAutoRefresh, setTickerAutoRefresh] = useState(true);
  useEffect(() => {
    if (!authenticated) return;
    const fetchMarket = async () => {
      try {
        const res = await axios.get("/api/zerodha/market-ticker", { timeout: 5000 });
        if (res.data.success) setMarketData(res.data.data);
      } catch {}
    };
    fetchMarket();
    let id = null;
    if (tickerAutoRefresh) id = setInterval(fetchMarket, 5 * 60 * 1000);
    return () => { if (id) clearInterval(id); };
  }, [authenticated, tickerAutoRefresh]);

  // -- RENDER --

  if (authGate === "loading") {
    return <div className="app" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><div className="spinner" /></div>;
  }
  if (authGate === "gate") {
    return <GoogleGate clientId={googleClientId} onSignedIn={(email) => { setUserEmail(email); setAuthGate("ok"); }} />;
  }

  if (step === "login") {
    return (
      <div className="app">
        <div className="login-container">
          <h2>Fib+VWAP Trading</h2>
          <p>Login with Zerodha to start</p>
          {error && <div className="error-msg">{error}</div>}
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleLogin} disabled={loading}>
              {loading ? "Redirecting..." : "Login with Zerodha"}
            </button>
          </div>
          <p style={{ marginTop: 16, fontSize: 12, color: "#607d8b" }}>
            You'll be redirected to Kite login page
          </p>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="app">
      <div className="header">
        <h1>
          Fib+VWAP <span>Trading</span>
          <span className="broker-badge">Zerodha</span>
        </h1>
        <div style={{display:"flex", gap:8, alignItems:"center"}}>
          <TradeMode />
            <button
            style={{
              padding: "6px 16px", fontWeight: 700, fontSize: 13, borderRadius: 6, border: "none", cursor: "pointer",
              background: killSwitch ? "#22c55e" : "#dc2626", color: "#fff",
            }}
            onClick={async () => {
              if (killSwitch) {
                await axios.post("/api/kill-switch/deactivate");
                setKillSwitch(false);
              } else {
                if (window.confirm("EMERGENCY STOP — This will block ALL broker orders immediately. Continue?")) {
                  await axios.post("/api/kill-switch/activate");
                  setKillSwitch(true);
                }
              }
            }}
            title={killSwitch ? "Kill switch is ON — click to resume trading" : "Emergency stop — block all broker orders"}
          >
            {killSwitch ? "RESUME TRADING" : "KILL SWITCH"}
          </button>
          <button
            style={{ padding: "6px 12px", fontWeight: 700, fontSize: 13, borderRadius: 6, border: "1px solid var(--border-strong)", cursor: "pointer", background: "transparent", color: "var(--text-muted)" }}
            onClick={() => setTheme(t => (t === "light" ? "dark" : "light"))}
            title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
          >{theme === "light" ? "🌙 Dark" : "☀️ Light"}</button>
          <button
            style={{ padding: "6px 12px", fontWeight: 700, fontSize: 13, borderRadius: 6, border: "1px solid var(--border-strong)", cursor: "pointer", background: "transparent", color: "var(--text-muted)" }}
            onClick={() => window.location.reload()}
            title="Refresh app"
          >Refresh</button>
          <button className="btn btn-troubleshoot" onClick={() => setShowTroubleshoot(true)} title="Troubleshooting Guide">?</button>
          {userEmail && (
            <button title={`Signed in as ${userEmail} — sign out`}
              style={{ padding: "6px 12px", fontWeight: 700, fontSize: 13, borderRadius: 6, border: "1px solid var(--border-strong)", cursor: "pointer", background: "transparent", color: "var(--text-muted)" }}
              onClick={async () => { await axios.post("/api/auth/logout").catch(() => {}); setUserEmail(null); setAuthGate("gate"); }}>
              Sign out
            </button>
          )}
          <button className="btn btn-danger" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* Kill Switch Banner */}
      {killSwitch && (
        <div style={{ background: "#dc2626", color: "#fff", padding: "10px 20px", textAlign: "center", fontWeight: 700, fontSize: 14 }}>
          KILL SWITCH ACTIVE — All broker orders are blocked. Click "RESUME TRADING" to deactivate.
        </div>
      )}

      {/* Troubleshooting Modal */}
      {showTroubleshoot && (
        <div className="confirm-overlay" onClick={() => setShowTroubleshoot(false)}>
          <div className="help-dialog troubleshoot-dialog" onClick={e => e.stopPropagation()}>
            <div className="help-header">
              <h3>Troubleshooting Guide</h3>
              <button className="chart-close" onClick={() => setShowTroubleshoot(false)}>X</button>
            </div>
            <div className="help-content">
              <div className="help-section">
                <h4>No Trades Being Placed</h4>
                <p>1. Is scanning active? (status bar shows "Auto-Scan ON")</p>
                <p>2. Is trading active? (status bar shows "PAPER TRADING" or "LIVE TRADING")</p>
                <p>3. Are signals found? (check signals count after scan)</p>
                <p>4. Direction filter blocking? (set to "Both" to test)</p>
                <p>5. Check server logs for <code>[Monitor]</code> entries.</p>
              </div>
              <div className="help-section">
                <h4>Authentication</h4>
                <p><strong>Login failed:</strong> Tokens expire daily — re-login each morning.</p>
                <p><strong>Redirect error:</strong> Check redirect URL in Kite developer console.</p>
              </div>
              <div className="help-section">
                <h4>Quick Fixes</h4>
                <table className="stocks-table" style={{fontSize:12}}>
                  <tbody>
                    <tr><td>Auth expired</td><td>Re-login (daily)</td></tr>
                    <tr><td>Duplicates</td><td><code>echo "[]" &gt; server/data/activeTrades.json</code></td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div style={{
        display: "flex", gap: 16, alignItems: "center", padding: "6px 16px",
        background: "var(--panel3)", fontSize: 12, fontWeight: 600, borderBottom: "1px solid #2a2a4a",
        flexWrap: "wrap",
      }}>
        {/* Zerodha Connection */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", display: "inline-block",
            background: authLive ? "#22c55e" : "#dc2626",
            boxShadow: authLive ? "0 0 6px #22c55e" : "0 0 6px #dc2626",
          }} />
          <span style={{ color: authLive ? "#22c55e" : "#dc2626" }}>
            {authLive ? "Zerodha Connected" : "Zerodha Disconnected"}
          </span>
        </div>

        <span style={{ color: "var(--btn-muted)", fontSize: 10 }}>|</span>

        {/* Market Status */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", display: "inline-block",
            background: monitorInfo?.marketOpen ? "#22c55e" : "var(--btn-border)",
          }} />
          <span style={{ color: monitorInfo?.marketOpen ? "#22c55e" : "var(--grey-text2)" }}>
            {monitorInfo?.marketOpen ? "Market Open" : "Market Closed"}
          </span>
        </div>

        <span style={{ color: "var(--btn-muted)", fontSize: 10 }}>|</span>

        {/* Strategy / Scanning Status */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", display: "inline-block",
            background: monitorInfo?.monitoring ? "#3b82f6" : "var(--btn-border)",
            animation: monitorInfo?.monitoring ? "pulse 2s infinite" : "none",
          }} />
          <span style={{ color: monitorInfo?.monitoring ? "#3b82f6" : "var(--grey-text2)" }}>
            {monitorInfo?.monitoring
              ? `Monitoring ${monitorInfo?.openCount || 0} trade${(monitorInfo?.openCount || 0) !== 1 ? "s" : ""}`
              : "Not Monitoring"
            }
          </span>
        </div>

        {/* Open/Closed counts */}
        {(monitorInfo?.openCount > 0 || monitorInfo?.closedCount > 0) && (
          <>
            <span style={{ color: "var(--btn-muted)", fontSize: 10 }}>|</span>
            <span style={{ color: "var(--grey-text)" }}>
              {monitorInfo?.openCount > 0 && <span style={{ color: "#f59e0b" }}>{monitorInfo.openCount} open</span>}
              {monitorInfo?.openCount > 0 && monitorInfo?.closedCount > 0 && " / "}
              {monitorInfo?.closedCount > 0 && <span style={{ color: "#6b7280" }}>{monitorInfo.closedCount} closed</span>}
            </span>
          </>
        )}

        {/* Server Clock — pushed to right */}
        {serverTime && (
          <span style={{ marginLeft: "auto", color: "var(--grey-text2)", fontFamily: "monospace", fontSize: 11, letterSpacing: 0.5 }}>
            {serverTime}
          </span>
        )}
      </div>

      {/* Market Ticker */}
      <div className="market-ticker">
        {marketData ? marketData.map((item, i) => (
          <div key={i} className={`ticker-item ${item.name === "INDIA VIX" ? "ticker-vix" : ""}`}>
            <span className="ticker-name">{item.name}</span>
            <span className="ticker-price">{item.ltp?.toFixed(item.name === "INDIA VIX" ? 2 : 1)}</span>
            <span className={`ticker-change ${item.change >= 0 ? "positive" : "negative"}`}>
              {item.change >= 0 ? "▲" : "▼"} {Math.abs(item.change)?.toFixed(2)} ({item.changePct >= 0 ? "+" : ""}{item.changePct?.toFixed(2)}%)
            </span>
          </div>
        )) : (
          <div className="ticker-item"><span className="ticker-name">Loading market data...</span></div>
        )}
        <button className={`ticker-refresh-btn ${tickerAutoRefresh ? "ticker-refresh-on" : "ticker-refresh-off"}`}
          onClick={() => setTickerAutoRefresh(prev => !prev)}
          title={tickerAutoRefresh ? "Auto-refresh ON (5 min) — click to pause" : "Auto-refresh OFF — click to resume"}>
          {tickerAutoRefresh ? "⟳ ON" : "⏸ OFF"}
        </button>
      </div>

      {alertBanner && (
        <div style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 3000,
          background: "#7c2d12", border: "2px solid #f59e0b", borderRadius: 10, padding: "12px 18px",
          boxShadow: "0 8px 30px rgba(0,0,0,.6)", maxWidth: "min(640px, 92vw)", display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 22 }}>🔔</span>
          <div>
            <div style={{ fontWeight: 700, color: "#fff", fontSize: 14 }}>PRICE ALERT — {alertBanner.symbol}</div>
            <div style={{ color: "#fde68a", fontSize: 12.5 }}>{alertBanner.msg}{alertBanner.note ? ` — ${alertBanner.note}` : ""}</div>
          </div>
          <button onClick={() => setAlertBanner(null)}
            style={{ marginLeft: 8, background: "#f59e0b", border: "none", borderRadius: 6, padding: "6px 12px", fontWeight: 700, cursor: "pointer" }}>
            Dismiss</button>
        </div>
      )}
      {/* Row 1 — Trading desks (live/sim instruments & strategies) */}
      <div className="nav-tabs nav-row1">
        <span className="nav-row-label">Trading</span>
        <button className={`nav-tab ${activeTab === "dashboard" ? "active" : ""}`}
          onClick={() => { setActiveTab("dashboard"); setDashUnseen(0); try { localStorage.setItem(DASH_SEEN_KEY, String(Date.now())); } catch {} }}
          style={{ fontWeight: 700, borderBottom: activeTab === "dashboard" ? "2px solid #22c55e" : undefined }}>
          Dashboard{dashUnseen > 0 && <span style={{ marginLeft: 5, background: "#dc2626", color: "#fff", borderRadius: 9, fontSize: 9.5, fontWeight: 700, padding: "1px 6px" }}>{dashUnseen}</span>}
        </button>
        {STRATEGY_TABS.map(t => (
          <button key={t.id} className={`nav-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
        ))}
        {showAllTabs && (<>
        <button className={`nav-tab ${activeTab === "smart2-nifty" ? "active" : ""}`} onClick={() => setActiveTab("smart2-nifty")}
          style={{borderBottom: activeTab === "smart2-nifty" ? "2px solid #8b5cf6" : undefined, color: activeTab === "smart2-nifty" ? "#8b5cf6" : undefined}}>
          NIFTY Smart
        </button>
        </>)}
        <button className={`nav-tab ${activeTab === "guarded-nifty" ? "active" : ""}`} onClick={() => setActiveTab("guarded-nifty")}
          style={{borderBottom: activeTab === "guarded-nifty" ? "2px solid #f59e0b" : undefined, color: activeTab === "guarded-nifty" ? "#f59e0b" : undefined}}>
          NIFTY Guarded
        </button>
        <button className={`nav-tab ${activeTab === "crypto" ? "active" : ""}`} onClick={() => setActiveTab("crypto")}
          style={{borderBottom: activeTab === "crypto" ? "2px solid #06b6d4" : undefined, color: activeTab === "crypto" ? "#06b6d4" : undefined}}>
          Crypto
        </button>
        {showAllTabs && (<>
        <button className={`nav-tab ${activeTab === "expiry" ? "active" : ""}`} onClick={() => setActiveTab("expiry")}
          style={{borderBottom: activeTab === "expiry" ? "2px solid #f43f5e" : undefined, color: activeTab === "expiry" ? "#f43f5e" : undefined}}>
          Expiry Day
        </button>
        <button className={`nav-tab ${activeTab === "gold" ? "active" : ""}`} onClick={() => setActiveTab("gold")}
          style={{borderBottom: activeTab === "gold" ? "2px solid #eab308" : undefined, color: activeTab === "gold" ? "#eab308" : undefined}}>
          Gold
        </button>
        </>)}
      </div>

      {/* Row 2 — Analytics & reports */}
      <div className="nav-tabs nav-row2">
        <span className="nav-row-label">Analytics</span>
        {ANALYTICS_TABS.filter(t => showAllTabs || CORE_TAB_IDS.has(t.id)).map(t => (
          <button key={t.id} className={`nav-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Row 3 — Labs, scanners & tools */}
      <div className="nav-tabs nav-row2">
        <span className="nav-row-label">Labs &amp; Tools</span>
        {UTIL_TABS.filter(t => showAllTabs || CORE_TAB_IDS.has(t.id)).map(t => (
          <button key={t.id} className={`nav-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
        ))}
        <button className={`nav-tab ${activeTab === "telegram" ? "active" : ""}`} onClick={() => setActiveTab("telegram")}
          style={{borderBottom: activeTab === "telegram" ? "2px solid #38bdf8" : undefined, color: activeTab === "telegram" ? "#38bdf8" : undefined}}>
          Telegram
        </button>
        <button className="nav-tab" onClick={toggleAllTabs}
          title={showAllTabs ? "Hide extra tabs (scanners keep running in the background)" : "Show all hidden tabs"}
          style={{marginLeft: "auto", color: "var(--dim2)", border: "1px dashed var(--faint2)", borderRadius: 6}}>
          {showAllTabs ? "▲ Hide Extras" : "⋯ More Tabs"}
        </button>
      </div>

      {STRATEGY_TABS.map(t => (
        <div key={t.id} style={{display: activeTab === t.id ? "block" : "none"}}>
          <FibVwap broker={broker} index={t.index} />
        </div>
      ))}

      <div style={{display: activeTab === "smart2-nifty" ? "block" : "none"}}>
        <FibVwapSmart2 broker={broker} index="NIFTY" />
      </div>

      <div style={{display: activeTab === "guarded-nifty" ? "block" : "none"}}>
        <FibVwapGuarded broker={broker} index="NIFTY" />
      </div>

      <div style={{display: activeTab === "expiry" ? "block" : "none"}}>
        <ExpiryDay />
      </div>

      <div style={{display: activeTab === "crypto" ? "block" : "none"}}>
        <CryptoTab />
      </div>

      {activeTab === "crypto-analytics" && <CryptoAnalytics />}
      {activeTab === "ob-tracker" && <OBTracker />}

      {activeTab === "events" && <EventsTab />}

      <div style={{display: activeTab === "gold" ? "block" : "none"}}>
        <GoldBacktest />
      </div>

      {activeTab === "strategy-scanner" && <StrategyScanner />}

      {activeTab === "strategy-options" && <StrategyOptions />}

      {activeTab === "mr-options" && <MROptions />}
      {activeTab === "breakout915" && <Breakout915 />}
      {activeTab === "reversal" && <Reversal />}
      {activeTab === "five-ema" && <FiveEma broker={broker} />}

      {activeTab === "fvg-scanner" && <FvgScanner />}

      {activeTab === "strategy-backtest" && <StrategyBacktest />}

      {activeTab === "smc-strategy" && <SMCStrategy />}

      {activeTab === "oi-momentum" && <OIMomentum />}

      {activeTab === "premium-analyzer" && <PremiumAnalyzer />}

      {activeTab === "sr-levels" && <SRLevels />}

      {activeTab === "alerts" && <TradeAlerts />}

      {activeTab === "ai-analyze" && <AiAnalyzer />}

      {activeTab === "unit-test" && <UnitTest />}

      {activeTab === "analytics" && <Analytics />}
      {activeTab === "pnl-calendar" && <PnlCalendar />}
      {activeTab === "never-red" && <NeverRed />}
      {activeTab === "price-alerts" && <PriceAlerts />}
      {activeTab === "dashboard" && <Dashboard />}
      {activeTab === "prem-ab" && <ShadowPrem />}
      {activeTab === "fin-targets" && <FinancialTargets />}
      {activeTab === "pnl-calc" && <PnlCalc />}

      {activeTab === "strategy-log" && <StrategyLog />}

      {activeTab === "logs" && <Logs />}

      {activeTab === "positions" && <Positions />}

      {activeTab === "telegram" && <TelegramSettings />}

    </div>
  );
}

function TelegramSettings() {
  const [config, setConfig] = useState(null);
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    axios.get("/api/telegram/config").then(r => {
      if (r.data.success) {
        const c = r.data.config;
        setConfig(c);
        setBotToken(c.bot_token || "");
        setChatId(c.chat_id || "");
        setEnabled(c.enabled || false);
      }
    }).catch(() => {});
  }, []);

  async function save() {
    const body = {
      enabled, chat_id: chatId,
      send_nifty: config?.send_nifty !== false,
      send_guarded: config?.send_guarded !== false,
      send_crypto: config?.send_crypto !== false,
      send_gold: config?.send_gold !== false,
      send_expiry: config?.send_expiry !== false,
      send_lifecycle: config?.send_lifecycle !== false,
      send_reversal: config?.send_reversal !== false,
      send_915: config?.send_915 !== false,
      send_mr: config?.send_mr !== false,
      send_ai_review: config?.send_ai_review !== false,
      send_daily_report: config?.send_daily_report !== false,
      send_morning_check: config?.send_morning_check !== false,
    };
    if (botToken && !botToken.startsWith("*")) body.bot_token = botToken;
    const r = await axios.post("/api/telegram/config", body);
    if (r.data.success) { setConfig(r.data.config); setMsg("Saved!"); }
  }

  async function test() {
    setTesting(true); setMsg("");
    const r = await axios.post("/api/telegram/test");
    setMsg(r.data.message);
    setTesting(false);
  }

  return (
    <div className="five-ema" style={{ maxWidth: 600 }}>
      <h2 style={{ fontSize: 16, margin: "8px 0" }}>Telegram Alerts</h2>
      <p style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 16 }}>
        Receive trade signals, entries, exits, and EOD notifications on Telegram.
        Create a bot via @BotFather, add it to a channel as admin, and paste the details below.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label style={{ fontSize: 12, color: "var(--dim2)" }}>
          Bot Token
          <input type="text" value={botToken} onChange={e => setBotToken(e.target.value)}
            placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
            style={{ width: "100%", padding: "8px 10px", marginTop: 4, background: "var(--panel3)", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--text2)", fontSize: 13 }} />
        </label>

        <label style={{ fontSize: 12, color: "var(--dim2)" }}>
          Chat ID (channel: @channelname or -100xxxxx)
          <input type="text" value={chatId} onChange={e => setChatId(e.target.value)}
            placeholder="@mychannel or -1001234567890"
            style={{ width: "100%", padding: "8px 10px", marginTop: 4, background: "var(--panel3)", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--text2)", fontSize: 13 }} />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text2)" }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enable Telegram Alerts
        </label>

        <div style={{ marginTop: 8, padding: "10px 12px", background: "var(--panel2)", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 8 }}>Send alerts for:</div>
          {[
            { key: "send_nifty", label: "NIFTY (original scanner — SIM trades)" },
            { key: "send_guarded", label: "NIFTY Guarded (live trades + EOD exits)" },
            { key: "send_crypto", label: "Crypto trader (Shark paper — entries, tier bookings, exits)" },
            { key: "send_gold", label: "Gold (XAUT/USDT scan signals)" },
            { key: "send_expiry", label: "Expiry Day (wall-bounce + gamma-breakout)" },
            { key: "send_lifecycle", label: "Trade lifecycle (tier bookings, breakeven locks, all exits)" },
            { key: "send_reversal", label: "Reversal paper scanner" },
            { key: "send_915", label: "915 Breakout paper scanner" },
            { key: "send_mr", label: "MR-Options paper scanner" },
            { key: "send_ai_review", label: "AI exit suggestions on open trades (advisory only)" },
            { key: "send_morning_check", label: "Morning readiness check (08:55 IST — incl. Kite login reminder)" },
            { key: "send_daily_report", label: "Daily EOD report (15:35 IST)" },
          ].map(({ key, label }) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>
              <input type="checkbox" checked={config?.[key] !== false}
                onChange={e => setConfig(prev => ({ ...prev, [key]: e.target.checked }))} />
              {label}
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary" onClick={save}>Save</button>
          <button className="btn btn-secondary" onClick={test} disabled={testing}>{testing ? "Sending..." : "Send Test Message"}</button>
        </div>

        {msg && <div style={{ padding: "8px 12px", background: "var(--panel3)", borderRadius: 6, fontSize: 12, color: msg.includes("Failed") ? "#ef4444" : "#22c55e" }}>{msg}</div>}
      </div>
    </div>
  );
}

export default App;


/* ===== Google sign-in gate (shown when auth is enabled and no session) ===== */
function GoogleGate({ clientId, onSignedIn }) {
  const btnRef = useRef(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const init = () => {
      if (!window.google || !window.google.accounts || !btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          try {
            const r = await axios.post("/api/auth/google", { credential: resp.credential });
            if (r.data.success) onSignedIn(r.data.email);
          } catch (e) {
            setErr(e.response?.data?.error === "email not allowed"
              ? "This Google account is not authorised for this app."
              : (e.response?.data?.error || "Sign-in failed"));
          }
        },
      });
      window.google.accounts.id.renderButton(btnRef.current, { theme: "filled_black", size: "large", shape: "pill" });
    };
    if (window.google && window.google.accounts) { init(); return; }
    const sc = document.createElement("script");
    sc.src = "https://accounts.google.com/gsi/client";
    sc.async = true;
    sc.onload = init;
    document.head.appendChild(sc);
  }, [clientId, onSignedIn]);
  return (
    <div className="app" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 12, padding: "36px 32px", textAlign: "center", maxWidth: 360 }}>
        <h1 style={{ fontSize: 22, marginBottom: 6 }}><span style={{ color: "var(--pos)" }}>Fib+VWAP</span> Trading</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 22 }}>Private terminal — sign in with an authorised Google account</p>
        <div ref={btnRef} style={{ display: "flex", justifyContent: "center" }} />
        {err && <p style={{ color: "var(--neg)", fontSize: 12, marginTop: 14 }}>{err}</p>}
      </div>
    </div>
  );
}
